import { useCallback, useEffect, useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { MessagesSquare, AlertTriangle, Loader2, Check, Send } from "lucide-react";
import { api } from "@/lib/api";
import { CONVERSATIONS_CHANGED } from "@/pages/admin/AdminLayout";

const when = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT") : "");
const PILL = "text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap";
const notifyMenu = () => window.dispatchEvent(new Event(CONVERSATIONS_CHANGED));

/* Conversas do cliente (por Assunto). Sem tempo real: recarrega após cada ação. */
export function LeadConversations({ lead, onResolved, onChanged, onClearLegacy }) {
    const [items, setItems] = useState(null);
    const [opened, setOpened] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/leads/${lead.id}/questions`);
            setItems(data);
            notifyMenu();
        } catch { setItems((cur) => cur || []); }
    }, [lead.id]);
    useEffect(() => { load(); }, [load]);

    const markRead = async (id) => {
        try {
            await api.post(`/admin/leads/${lead.id}/questions/${id}/read`);
            setItems((cur) => cur.map((x) => (x.id === id ? { ...x, unread: false } : x)));
            onChanged();
            notifyMenu();
        } catch { /* mantém o indicador */ }
    };
    const onOpenChange = (next) => {
        next.filter((id) => !opened.includes(id) && items.find((x) => x.id === id)?.unread).forEach(markRead);
        setOpened(next);
    };
    const resolve = async (id) => {
        try {
            const { data } = await api.post(`/admin/leads/${lead.id}/questions/${id}/resolve`);
            toast.success("Conversa marcada como resolvida.");
            onResolved(data.has_doubt);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao marcar como resolvida");
        }
    };

    const list = items || [];
    const open = list.filter((c) => c.status === "open").length;
    const unread = list.filter((c) => c.unread).length;
    const legacy = items !== null && lead.has_doubt && open === 0 && lead.status !== "Adjudicado";

    return (
        <div className="bg-white rounded-lg border border-stone-200 p-4 md:p-5" data-testid="lead-conversations">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <MessagesSquare className="w-4 h-4 text-[hsl(var(--copper))]" />
                <span className="text-xs uppercase tracking-wider text-stone-700 font-semibold">Conversas do cliente</span>
                {unread > 0 && <span className={`${PILL} bg-[hsl(var(--copper))] text-white`} data-testid="lead-conversations-unread">{unread === 1 ? "1 nova mensagem" : `${unread} novas mensagens`}</span>}
                {open > 0 && <span className={`${PILL} bg-amber-100 text-amber-800`} data-testid="lead-conversations-open">{open} em aberto</span>}
            </div>
            <p className="text-xs text-stone-500 mb-4">
                O cliente inicia as conversas na Área do Cliente, cada uma com o seu assunto. As mensagens enviadas não podem ser editadas nem apagadas.
            </p>
            {legacy && <LegacyFlag onClear={onClearLegacy} />}
            {items === null && <Loader2 className="w-4 h-4 animate-spin text-stone-400" />}
            {items !== null && list.length === 0 && (
                <p className="text-sm text-stone-400 italic" data-testid="lead-conversations-empty">Ainda não há conversas.</p>
            )}
            {list.length > 0 && (
                <Accordion type="multiple" value={opened} onValueChange={onOpenChange} className="border border-stone-200 rounded-lg divide-y divide-stone-100" data-testid="lead-conversations-accordion">
                    {list.map((c) => (
                        <ConversationItem key={c.id} leadId={lead.id} c={c} hasEmail={!!(lead.email || "").includes("@")} onResolve={resolve} onReplied={load} />
                    ))}
                </Accordion>
            )}
        </div>
    );
}

function LegacyFlag({ onClear }) {
    return (
        <div className="mb-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="lead-doubt-flag">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
                <div className="text-amber-900 font-semibold">O cliente tem uma questão</div>
                <p className="text-amber-800 text-xs mt-0.5">Questão antiga, sem texto registado. Contacte o cliente pelos meios habituais.</p>
            </div>
            <button type="button" onClick={onClear} className="text-xs text-amber-900 underline hover:no-underline" data-testid="lead-doubt-clear">
                Marcar como resolvida
            </button>
        </div>
    );
}

function ConversationItem({ leadId, c, hasEmail, onResolve, onReplied }) {
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(null);
    const open = c.status === "open";
    const send = async () => {
        const text = body.trim();
        if (!text || busy) return;
        setBusy("send");
        try {
            await api.post(`/admin/leads/${leadId}/questions/${c.id}/messages`, { body: text });
            setBody("");
            toast.success(hasEmail ? "Resposta enviada. O cliente foi avisado por email." : "Resposta enviada.");
            await onReplied();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao enviar a resposta");
        } finally { setBusy(null); }
    };
    const resolve = async () => { setBusy("resolve"); await onResolve(c.id); setBusy(null); };
    return (
        <AccordionItem value={c.id} className="border-b-0 px-3" data-testid={`lead-conversation-${c.id}`}>
            <AccordionTrigger className="py-3 gap-3 hover:no-underline" data-testid={`lead-conversation-toggle-${c.id}`}>
                <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-semibold text-stone-900 truncate" data-testid={`lead-conversation-title-${c.id}`}>{c.title}</div>
                    <div className="text-[11px] text-stone-500 font-normal mt-0.5">
                        {when(c.last_message_at)} · {c.messages.length} {c.messages.length === 1 ? "mensagem" : "mensagens"}
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5">
                    {c.unread && <span className={`${PILL} bg-[hsl(var(--copper))] text-white`} data-testid={`lead-conversation-unread-${c.id}`}>Nova mensagem</span>}
                    <span className={`${PILL} ${open ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`lead-conversation-status-${c.id}`}>
                        {open ? "Em aberto" : "Resolvida"}
                    </span>
                </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
                <ol className="space-y-2.5" data-testid={`lead-conversation-messages-${c.id}`}>
                    {c.messages.map((m) => <AdminBubble key={m.id} m={m} />)}
                </ol>
                {c.resolved_at && <p className="text-xs text-stone-500 mt-3">Resolvida em {when(c.resolved_at)}</p>}
                <textarea
                    rows={3}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={2000}
                    placeholder="Escreva a resposta ao cliente"
                    className="mt-4 w-full px-3 py-2 border border-stone-300 rounded text-sm resize-none"
                    data-testid={`lead-conversation-reply-${c.id}`}
                />
                <p className="text-[11px] text-stone-500 mt-1">
                    {hasEmail ? "O cliente recebe um email a avisar que tem uma nova mensagem (sem o texto)." : "Este cliente não tem email: a resposta fica visível apenas na Área do Cliente."}
                </p>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <button type="button" onClick={send} disabled={!body.trim() || !!busy} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[hsl(var(--forest))] text-white text-xs font-semibold uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-40" data-testid={`lead-conversation-send-${c.id}`}>
                        {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Enviar resposta
                    </button>
                    {open && (
                        <button type="button" onClick={resolve} disabled={!!busy} className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-emerald-300 text-emerald-800 bg-emerald-50 text-xs font-semibold uppercase tracking-wider hover:bg-emerald-100 transition-colors disabled:opacity-40" data-testid={`lead-conversation-resolve-${c.id}`}>
                            {busy === "resolve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Marcar como resolvida
                        </button>
                    )}
                </div>
            </AccordionContent>
        </AccordionItem>
    );
}

function AdminBubble({ m }) {
    const team = m.author === "team";
    return (
        <li className={`flex ${team ? "justify-end" : "justify-start"}`} data-testid={`lead-message-${m.id}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 ${team ? "bg-emerald-50 border border-emerald-100" : "bg-stone-100"}`}>
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${team ? "text-emerald-800" : "text-[hsl(var(--copper))]"}`}>{team ? "Equipa" : "Cliente"}</div>
                <p className="text-sm text-stone-800 whitespace-pre-wrap break-words leading-relaxed mt-0.5">{m.body}</p>
                <div className="text-[10px] text-stone-500 mt-1">{when(m.created_at)}</div>
            </div>
        </li>
    );
}
